// Copyright (c) Ping. All rights reserved.
//
// Unified Emoji + GIF picker overlay. Pixel-pinned to Figma
// `2804:26810` "Emoji container" (370×501) — the same popover at
// `2804:27348` "GIF container" (370×456) is the SAME frame with the
// GIFs tab active. The Figma source has THREE identical tabs across
// both: `Emoji / GIFs / Stickers`. There is no separate GIF trigger
// button in the composer — the user opens this popover via the
// existing Smile affordance and switches panes via the tab bar.
//
// Layout (Figma `2804:26810`):
//   - Outer: 370 × 501, radius 16, fill `#11131A` (surface.primary)
//   - itemSpacing: 16
//   - Header section `3154:7776`: padding L16/R16/T16, itemSpacing 16
//   - Tab bar `2804:26811`: 338×36, padding 4, radius 10000,
//     fill `#15171C` (surface.tertiary)
//   - Tab pill `2804:26812`: 110×28, paddings L16/R16/T4/B4,
//     radius 1000. Active fill `#222429`, inactive transparent.
//   - Active text Lato 700 14/19.6 `#F5F6F8`,
//     inactive Lato 700 14/19.6 `#B5BAC3`
//   - Search input `2804:26817`: 338×40, padding 10, radius 12,
//     fill `#222429`, placeholder `Search Emoji` / `Search GIF`
//   - Emoji grid `2804:26823`: 6-col glyph grid

import React, { useMemo, useState } from 'react';
import { Platform, Pressable, ScrollView, StyleSheet, Text, type TextStyle, TextInput, View, type ViewStyle } from 'react-native';
import { useColorScheme } from '../../foundations/colorScheme';
import {
  Apple,
  Car,
  Clock,
  Flag,
  Heart,
  Lightbulb,
  Music,
  Smile,
  Volleyball,
  type LucideIcon,
} from 'lucide-react-native';
import { tokens } from '../../design-system/tokens';
import { GifPanel } from './ComposerGifOverlay';
import type { GifItem } from './GifSearchProvider';

export interface ComposerEmojiOverlayItem {
  readonly glyph: string;
  readonly shortcode: string;
  /** Optional group key used for section grouping in the grid. */
  readonly group?: string | undefined;
}

// Footer category rail — Figma `2804:26810` shows a 9-icon row across
// the bottom of the popover. The icons are pure navigation hints; they
// don't drive filtering yet but match the design's visual rhythm.
interface FooterCategory {
  readonly key: string;
  readonly Icon: LucideIcon;
  readonly label: string;
}
const FOOTER_CATEGORIES: readonly FooterCategory[] = [
  { key: 'recent', Icon: Clock, label: 'Recent' },
  { key: 'smileys', Icon: Smile, label: 'Smileys & People' },
  { key: 'animals', Icon: Apple, label: 'Animals & Nature' },
  { key: 'food', Icon: Apple, label: 'Food & Drink' },
  { key: 'activities', Icon: Volleyball, label: 'Activities' },
  { key: 'travel', Icon: Car, label: 'Travel & Places' },
  { key: 'objects', Icon: Lightbulb, label: 'Objects' },
  { key: 'symbols', Icon: Heart, label: 'Symbols' },
  { key: 'flags', Icon: Flag, label: 'Flags' },
  { key: 'music', Icon: Music, label: 'Music' },
];

// Group-label → readable section title. Falls through to a Title-Case
// version of the group key for unknown groups.
const GROUP_LABELS: Readonly<Record<string, string>> = {
  recent: 'Recent',
  smileys: 'Smileys & People',
  people: 'Smileys & People',
  animals: 'Animals & Nature',
  food: 'Food & Drink',
  activities: 'Activities',
  travel: 'Travel & Places',
  objects: 'Objects',
  symbols: 'Symbols',
  flags: 'Flags',
};

function formatGroupLabel(group: string): string {
  return (
    GROUP_LABELS[group] ??
    group.replace(/^./, (c) => c.toUpperCase())
  );
}

export type ComposerEmojiOverlayTab = 'emoji' | 'gifs' | 'stickers';

export interface ComposerEmojiOverlayProps {
  readonly query: string;
  readonly items: ReadonlyArray<ComposerEmojiOverlayItem>;
  readonly onChangeQuery: (next: string) => void;
  readonly onSelect: (item: ComposerEmojiOverlayItem) => void;
  readonly onClose: () => void;
  readonly testID?: string | undefined;
  // ---- GIF tab wiring (optional) ----
  // When the host supplies `gifItems` + `gifQuery` + `onChangeGifQuery`
  // + `onSelectGif`, the tab bar's GIFs pill is interactive and the
  // GIF panel renders when the user switches to it. When omitted the
  // pill is disabled with a 50% opacity hint.
  readonly gifItems?: ReadonlyArray<GifItem> | undefined;
  readonly gifQuery?: string | undefined;
  readonly onChangeGifQuery?: ((q: string) => void) | undefined;
  readonly onSelectGif?: ((gif: GifItem) => void) | undefined;
  // ---- Stickers tab wiring (optional, mirrors GIF wiring) ----
  readonly stickerItems?: ReadonlyArray<GifItem> | undefined;
  readonly stickerQuery?: string | undefined;
  readonly onChangeStickerQuery?: ((q: string) => void) | undefined;
  readonly onSelectSticker?: ((sticker: GifItem) => void) | undefined;
  // Initial active tab — defaults to `emoji`. The composer can pass
  // `gifs` to open the popover already on the GIF tab if needed.
  readonly initialTab?: ComposerEmojiOverlayTab | undefined;
}

interface TabSpec {
  readonly key: ComposerEmojiOverlayTab;
  readonly label: string;
  readonly enabled: boolean;
}

export const ComposerEmojiOverlay = React.memo<ComposerEmojiOverlayProps>(
  ({
    query,
    items,
    onChangeQuery,
    onSelect,
    onClose,
    testID,
    gifItems,
    gifQuery,
    onChangeGifQuery,
    onSelectGif,
    stickerItems,
    stickerQuery,
    onChangeStickerQuery,
    onSelectSticker,
    initialTab,
  }) => {
    const scheme = useColorScheme();
    const colorScheme: 'light' | 'dark' = scheme === 'dark' ? 'dark' : 'light';
    const colors = tokens.color.semantic[colorScheme];

    const gifEnabled =
      gifItems !== undefined &&
      gifQuery !== undefined &&
      onChangeGifQuery !== undefined &&
      onSelectGif !== undefined;

    const stickersEnabled =
      stickerItems !== undefined &&
      stickerQuery !== undefined &&
      onChangeStickerQuery !== undefined &&
      onSelectSticker !== undefined;

    const [tab, setTab] = useState<ComposerEmojiOverlayTab>(initialTab ?? 'emoji');

    const tabs: readonly TabSpec[] = [
      { key: 'emoji', label: 'Emoji', enabled: true },
      { key: 'gifs', label: 'GIFs', enabled: gifEnabled },
      { key: 'stickers', label: 'Stickers', enabled: stickersEnabled },
    ] as const;

    const placeholderColor = colorScheme === 'dark' ? '#B5BAC3' : colors.text.tertiary;
    const popoverBg = colorScheme === 'dark' ? '#111318' : colors.surface.primary;
    const popoverBorder = colorScheme === 'dark' ? '#2F333B' : colors.border.secondary;
    const tabBarBg = colorScheme === 'dark' ? '#15171C' : colors.surface.tertiary;
    const activePillBg = colorScheme === 'dark' ? '#222429' : colors.surface.secondary;
    const inputBg = colorScheme === 'dark' ? '#222429' : colors.surface.secondary;
    const activeText = colors.text.primary;
    const inactiveText = colorScheme === 'dark' ? '#B5BAC3' : colors.text.secondary;
    const dividerColor = colorScheme === 'dark' ? 'rgba(255,255,255,0.06)' : colors.border.secondary;

    // Track which emoji the user last focused so the bottom preview
    // row (Figma `2804:26810`) can show its name + shortcode.
    const [focused, setFocused] = useState<ComposerEmojiOverlayItem | null>(
      () => (items[0] ?? null) as ComposerEmojiOverlayItem | null,
    );

    // Group items into sections — Figma shows category headers
    // (`RECENT`, `SMILES & PEOPLE`). When items have no `group` the
    // whole list collapses into a single unlabelled section so search
    // results still render cleanly.
    const sections = useMemo<readonly { title: string | null; items: ComposerEmojiOverlayItem[] }[]>(() => {
      const out: { title: string | null; items: ComposerEmojiOverlayItem[] }[] = [];
      const map = new Map<string | null, ComposerEmojiOverlayItem[]>();
      for (const item of items) {
        const key = item.group ?? null;
        const list = map.get(key);
        if (list === undefined) {
          const next: ComposerEmojiOverlayItem[] = [item];
          map.set(key, next);
          out.push({ title: key === null ? null : formatGroupLabel(key), items: next });
        } else {
          list.push(item);
        }
      }
      return out;
    }, [items]);

    // Figma `2804:26810`: 370 × 501, radius 16, hairline border.
    // Outer chrome shared by all three tab branches.
    const overlayClasses =
      'w-[370px] max-w-full h-[501px] rounded-lg overflow-hidden flex flex-col border';
    const overlayStyle = {
      backgroundColor: popoverBg,
      borderColor: popoverBorder,
      borderWidth: StyleSheet.hairlineWidth,
    } as ViewStyle;

    // Figma `3154:7776` header section: padding L16/R16/T16,
    // itemSpacing 16, hosts the tab bar + search input.
    const headerClasses = 'px-lg pt-lg gap-lg';

    // Render the GIF panel as a self-contained component so it
    // owns its own grid layout / search row.
    if (tab === 'gifs' && gifEnabled) {
      return (
        <View className={overlayClasses} style={overlayStyle} testID={testID}>
          <View className={headerClasses}>
            <TabBar
              tabs={tabs}
              activeTab={tab}
              onChangeTab={setTab}
              tabBarBg={tabBarBg}
              activePillBg={activePillBg}
              activeText={activeText}
              inactiveText={inactiveText}
            />
          </View>
          <View className="flex-1 px-lg pb-lg">
            <GifPanel
              items={gifItems}
              query={gifQuery}
              onChangeQuery={onChangeGifQuery}
              onSelect={onSelectGif}
              onClose={onClose}
              searchPlaceholder="Search GIF"
            />
          </View>
        </View>
      );
    }

    // Stickers tab — reuses the GIF grid layout with sticker-only
    // items and its own query buffer.
    if (tab === 'stickers' && stickersEnabled) {
      return (
        <View className={overlayClasses} style={overlayStyle} testID={testID}>
          <View className={headerClasses}>
            <TabBar
              tabs={tabs}
              activeTab={tab}
              onChangeTab={setTab}
              tabBarBg={tabBarBg}
              activePillBg={activePillBg}
              activeText={activeText}
              inactiveText={inactiveText}
            />
          </View>
          <View className="flex-1 px-lg pb-lg">
            <GifPanel
              items={stickerItems}
              query={stickerQuery}
              onChangeQuery={onChangeStickerQuery}
              onSelect={onSelectSticker}
              onClose={onClose}
              searchPlaceholder="Search Stickers"
            />
          </View>
        </View>
      );
    }

    return (
      <View className={overlayClasses} style={overlayStyle} testID={testID}>
        <View className={headerClasses}>
          <TabBar
            tabs={tabs}
            activeTab={tab}
            onChangeTab={setTab}
            tabBarBg={tabBarBg}
            activePillBg={activePillBg}
            activeText={activeText}
            inactiveText={inactiveText}
          />
          {/*
            Figma `2804:26817`: 338×40 search input, padding 12,
            radius 12. `outlineStyle: 'none'` is the web-only focus-
            ring kill; cast keeps native typings happy.
          */}
          <View className="flex flex-row items-center gap-sm">
            <TextInput
              className="
                flex-1 h-10 px-md py-0 rounded-[12px]
                font-sans text-[14px] leading-5
              "
              style={[
                { color: colors.text.primary, backgroundColor: inputBg },
                // `outlineStyle` is a web-only CSS prop; RN-macOS rejects it
                // ("Invalid props.style key `outlineStyle`"), so gate on web.
                ...(Platform.OS === 'web'
                  ? [{ outlineStyle: 'none' } as unknown as TextStyle]
                  : []),
              ]}
              value={query}
              onChangeText={onChangeQuery}
              placeholder="Search Emoji"
              placeholderTextColor={placeholderColor}
              accessibilityLabel="Search emoji"
            />
          </View>
        </View>

        {/*
          Figma `2804:26823` "container with scroll line": grouped
          grid with section headers. Cells render WITHOUT a fill so
          emojis float on the popover surface.
        */}
        {items.length === 0 ? (
          <Text
            className="font-sans text-[14px] text-center py-xxl"
            style={{ color: colors.text.tertiary }}
          >
            No emoji found.
          </Text>
        ) : (
          <ScrollView
            className="flex-1"
            contentContainerStyle={{ paddingHorizontal: 16, paddingTop: 12, paddingBottom: 12 }}
            showsVerticalScrollIndicator={false}
          >
            {sections.map((section, sectionIdx) => (
              <View
                // biome-ignore lint/suspicious/noArrayIndexKey: section identity is stable
                key={`section-${sectionIdx}`}
                className="mb-xs"
              >
                {section.title !== null ? (
                  <Text
                    className="font-sans text-[11px] leading-[14px] font-bold py-sm"
                    style={{ color: inactiveText, letterSpacing: 0.6 }}
                    accessibilityRole="header"
                  >
                    {section.title.toUpperCase()}
                  </Text>
                ) : null}
                <View className="flex flex-row flex-wrap">
                  {section.items.map((item) => (
                    <Pressable
                      key={`${item.shortcode}-${item.glyph}`}
                      onPress={() => {
                        setFocused(item);
                        onSelect(item);
                      }}
                      onHoverIn={() => setFocused(item)}
                      accessibilityRole="button"
                      accessibilityLabel={`Insert ${item.shortcode}`}
                      // 6 cols at 338px popover ≈ 56px each.
                      // NativeWind doesn't have a `w-1/6` token so we
                      // inline the percentage.
                      className="aspect-square items-center justify-center rounded-sm"
                      style={{ width: '16.666%' }}
                    >
                      <Text className="text-[24px] leading-[28px]">{item.glyph}</Text>
                    </Pressable>
                  ))}
                </View>
              </View>
            ))}
          </ScrollView>
        )}

        {/*
          Figma `2804:26810` footer — focused-emoji preview row +
          9-icon category rail.
        */}
        <View
          className="border-t px-lg py-[10px] gap-sm"
          style={{ borderTopColor: dividerColor, borderTopWidth: StyleSheet.hairlineWidth }}
        >
          {focused !== null ? (
            <View className="flex flex-row items-center gap-[10px]">
              <Text className="text-[24px] leading-[28px]">{focused.glyph}</Text>
              <View className="flex-1 flex flex-col min-w-0">
                <Text
                  className="font-sans text-[14px] leading-[18px] font-semibold"
                  style={{ color: activeText }}
                  numberOfLines={1}
                >
                  {formatEmojiName(focused.shortcode)}
                </Text>
                <Text
                  className="font-sans text-[12px] leading-4"
                  style={{ color: inactiveText }}
                  numberOfLines={1}
                >
                  :{focused.shortcode}:
                </Text>
              </View>
            </View>
          ) : null}
          <View className="flex flex-row items-center justify-between">
            {FOOTER_CATEGORIES.slice(0, 9).map((cat) => {
              const Icon = cat.Icon;
              return (
                <View
                  key={cat.key}
                  className="w-7 h-7 items-center justify-center"
                >
                  <Icon size={16} strokeWidth={2} color={inactiveText} />
                </View>
              );
            })}
          </View>
        </View>
      </View>
    );
  },
);

// Render a friendly version of the shortcode. `grinning face` →
// `Grinning Face`; multi-word names are title-cased.
function formatEmojiName(shortcode: string): string {
  return shortcode
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

ComposerEmojiOverlay.displayName = 'ComposerEmojiOverlay';

interface TabBarProps {
  readonly tabs: readonly TabSpec[];
  readonly activeTab: ComposerEmojiOverlayTab;
  readonly onChangeTab: (next: ComposerEmojiOverlayTab) => void;
  readonly tabBarBg: string;
  readonly activePillBg: string;
  readonly activeText: string;
  readonly inactiveText: string;
}

// Figma `2804:26811`: 338×36, paddings 4, radius 10000, fill
// `#15171C`. Each child pill flex-1, height 28, paddings L16/R16/T4/B4,
// radius 1000.
const TabBar: React.FC<TabBarProps> = ({
  tabs,
  activeTab,
  onChangeTab,
  tabBarBg,
  activePillBg,
  activeText,
  inactiveText,
}) => {
  return (
    <View
      className="flex flex-row items-center h-9 p-xs gap-xs rounded-[1000px]"
      style={{ backgroundColor: tabBarBg }}
      accessibilityRole="tablist"
    >
      {tabs.map((spec) => {
        const isActive = spec.key === activeTab;
        return (
          <Pressable
            key={spec.key}
            onPress={() => {
              if (spec.enabled) onChangeTab(spec.key);
            }}
            disabled={!spec.enabled}
            accessibilityRole="tab"
            accessibilityState={{ selected: isActive, disabled: !spec.enabled }}
            accessibilityLabel={spec.label}
            testID={`composer-emoji-tab-${spec.key}`}
            className={`
              flex-1 h-7 px-lg py-xs rounded-[1000px]
              items-center justify-center
              ${!spec.enabled ? 'opacity-40' : ''}
            `}
            style={isActive ? { backgroundColor: activePillBg } : undefined}
          >
            <Text
              className="font-sans text-[14px] leading-5 font-bold"
              style={{ color: isActive ? activeText : inactiveText }}
              numberOfLines={1}
            >
              {spec.label}
            </Text>
          </Pressable>
        );
      })}
    </View>
  );
};

